/**
 * Configuration Operations Service
 *
 * PURPOSE:
 * Centralized, reusable operations for configuration management including
 * cloning, updating, and quick actions. Used by workflow pages.
 *
 * ARCHITECTURE:
 * UI Components → Store → This Service → ProjectOrchestrator
 *
 * KEY PRINCIPLES:
 * - Single source of truth for configuration operations
 * - Reusable across different UI contexts
 * - Pure functions with no side effects
 * - Returns data for store to manage state
 */

import { ConfigurationManager, ProjectOrchestrator } from '~/engine/orchestration';
import { logger } from '~/services/logging';
import { buildCustomizationMetadata, cloneOverrides } from '~/services/override-service';
import type { BondSeries, DRFInputsForm, MillageInputsForm, Project } from '~/types';
import { TargetType } from '~/types';
import type {
  ConfigurationOverrides,
  MillageOverrides,
  TaxableValueOverrides,
} from '~/types/overrides';

export interface ConfigurationChanges {
  millageInputs?: Partial<MillageInputsForm>;
  drfInputs?: Partial<DRFInputsForm>;
  bondSeries?: BondSeries[];
  /** Override values to apply (taxable values, millage rates) */
  overrides?: ConfigurationOverrides | null;
  /** Optional custom name for the new configuration */
  configName?: string;
}

/**
 * Clones a configuration with partial changes applied
 *
 * @param project - Current project state
 * @param baseConfigurationId - ID of configuration to clone
 * @param changes - Partial changes to apply to the clone
 * @returns Object with updated project and new configuration ID, or null if failed
 */
export function cloneConfigurationWithChanges(
  project: Project,
  baseConfigurationId: string,
  changes: ConfigurationChanges,
  context?: { scenarioId: string; actionId: string } | null
): { updatedProject: Project; newConfigurationId: string } | null {
  const currentConfigurations = project.configurations || [];
  const baseConfiguration = currentConfigurations.find(
    (config) => config.id === baseConfigurationId
  );

  if (!baseConfiguration) {
    logger.L2.debug(`Cannot clone - configuration ${baseConfigurationId} not found`, {
      context: 'store:storeOperations',
    });
    return null;
  }

  // Merge changes with BASE CONFIGURATION values, not project values
  const mergedMillage = changes.millageInputs
    ? ({
        ...baseConfiguration.millageInputs,
        ...changes.millageInputs,
      } as MillageInputsForm)
    : baseConfiguration.millageInputs;

  const mergedDrf = changes.drfInputs
    ? ({
        ...baseConfiguration.drfInputs,
        ...changes.drfInputs,
      } as DRFInputsForm)
    : baseConfiguration.drfInputs;

  // Build the updated project object with merged values
  const updatedProject: Project = {
    ...project,
    millageInputs: mergedMillage,
    drfInputs: mergedDrf,
    lastUpdated: new Date().toISOString(),
  };

  // Use provided bonds or clone from base
  const bondsToUse = changes.bondSeries || [...baseConfiguration.bondSeries];

  // Force creation of new configuration (bypass shouldCreateConfiguration check)
  // We need to directly use the configuration manager to ensure a new ID is generated
  // Calculate the next orderId by finding the highest existing orderId
  const highestOrderId = currentConfigurations.reduce(
    (max, config) => Math.max(max, config.orderId || 0),
    0
  );

  const configurationManager = new ConfigurationManager();
  const newConfiguration = configurationManager.createConfiguration(
    updatedProject,
    bondsToUse,
    highestOrderId, // This will be incremented by 1 inside createConfiguration
    context || undefined // Pass context if provided
  );

  // Handle overrides: either use provided overrides, or inherit from base configuration
  const isInheritingOverrides = changes.overrides === undefined;
  const overridesToApply = isInheritingOverrides
    ? cloneOverrides(baseConfiguration.overrides) // Inherit from base
    : changes.overrides; // Explicitly set (could be null to clear)

  // Apply overrides and metadata to new configuration
  // Only set inheritedFrom if we actually inherited overrides from base
  newConfiguration.overrides = overridesToApply;
  newConfiguration.customizationMetadata = buildCustomizationMetadata(
    overridesToApply,
    isInheritingOverrides && overridesToApply ? baseConfigurationId : undefined
  );

  // Apply custom name if provided
  if (changes.configName) {
    newConfiguration.name = changes.configName;
  }

  logger.L2.debug(`Cloned configuration ${baseConfigurationId} -> ${newConfiguration.id}`, {
    context: 'store:storeOperations',
    overridesApplied: !!overridesToApply,
  });

  // Manage configuration lifecycle (cleanup)
  const orchestrator = new ProjectOrchestrator();
  const newConfigurations = [...currentConfigurations, newConfiguration];
  const cleanedConfigurations = orchestrator.manageConfigurations(newConfigurations, 3);

  // Return updated project with cleaned configurations
  return {
    updatedProject: {
      ...updatedProject,
      configurations: cleanedConfigurations,
    },
    newConfigurationId: newConfiguration.id,
  };
}

/**
 * Quick action: Updates millage values for a configuration
 */
export function cloneWithMillageChanges(
  project: Project,
  configurationId: string,
  millageUpdates: Partial<MillageInputsForm>
): { updatedProject: Project; newConfigurationId: string } | null {
  return cloneConfigurationWithChanges(project, configurationId, {
    millageInputs: millageUpdates,
  });
}

/**
 * Creates a new configuration with taxable value overrides applied
 *
 * Used when users customize taxable values via the modal.
 * The new configuration will use the overridden values in revenue calculations.
 * Name is automatically set by the configuration factory.
 *
 * @param project - Current project state
 * @param baseConfigurationId - ID of configuration to clone
 * @param taxableValueOverrides - Taxable value overrides to apply
 * @returns Updated project and new configuration ID, or null if failed
 */
export function cloneConfigurationWithTaxableValueOverrides(
  project: Project,
  baseConfigurationId: string,
  taxableValueOverrides: TaxableValueOverrides
): { updatedProject: Project; newConfigurationId: string } | null {
  // Get base configuration to inherit any existing millage overrides
  const baseConfiguration = project.configurations?.find(
    (config) => config.id === baseConfigurationId
  );

  const overrides: ConfigurationOverrides = {
    taxableValue: taxableValueOverrides,
    // Preserve any existing millage overrides from base
    millage: baseConfiguration?.overrides?.millage ?? null,
  };

  return cloneConfigurationWithChanges(project, baseConfigurationId, {
    overrides,
  });
}

/**
 * Creates a new configuration with millage rate overrides applied
 *
 * Used when users customize millage rates via the modal.
 * The new configuration will use the overridden rates in revenue calculations.
 * Name is automatically set by the configuration factory.
 *
 * @param project - Current project state
 * @param baseConfigurationId - ID of configuration to clone
 * @param millageOverrides - Millage rate overrides to apply
 * @returns Updated project and new configuration ID, or null if failed
 */
export function cloneConfigurationWithMillageOverrides(
  project: Project,
  baseConfigurationId: string,
  millageOverrides: MillageOverrides
): { updatedProject: Project; newConfigurationId: string } | null {
  // Get base configuration to inherit any existing taxable value overrides
  const baseConfiguration = project.configurations?.find(
    (config) => config.id === baseConfigurationId
  );

  const overrides: ConfigurationOverrides = {
    // Preserve any existing taxable value overrides from base
    taxableValue: baseConfiguration?.overrides?.taxableValue ?? null,
    millage: millageOverrides,
  };

  return cloneConfigurationWithChanges(project, baseConfigurationId, {
    overrides,
  });
}

/**
 * Quick action: Shifts a bond's issue date by a number of years
 *
 * @param project - Current project state
 * @param configurationId - Configuration containing the bond
 * @param bondId - ID of the bond to shift
 * @param shiftYears - Number of years to shift (negative for earlier, positive for later)
 * @returns Updated project and new configuration ID, or null if validation fails
 */
export function cloneWithBondShift(
  project: Project,
  configurationId: string,
  bondId: string,
  shiftYears: number
): { updatedProject: Project; newConfigurationId: string } | null {
  const configuration = project.configurations?.find((config) => config.id === configurationId);
  if (!configuration) {
    logger.L2.debug(`Configuration ${configurationId} not found`, {
      context: 'store:storeOperations',
    });
    return null;
  }

  const bondIndex = configuration.bondSeries.findIndex((b) => b.id === bondId);
  if (bondIndex === -1) {
    logger.L2.debug(`Bond ${bondId} not found in configuration`, {
      context: 'store:storeOperations',
    });
    return null;
  }

  // Clone bonds array and modify the target bond
  const modifiedBonds = [...configuration.bondSeries];
  const targetBond = { ...modifiedBonds[bondIndex] };

  // Shift the issue date
  const currentIssueDate = new Date(targetBond.issueDate);
  currentIssueDate.setFullYear(currentIssueDate.getFullYear() + shiftYears);
  targetBond.issueDate = currentIssueDate.toISOString().split('T')[0];

  // Validate the shift doesn't violate project constraints
  // TODO: I want to look into this more.
  if (project.dateInputs?.paymentDates?.[0]) {
    const firstRevenue = new Date(project.dateInputs.paymentDates[0].date);
    if (currentIssueDate < firstRevenue) {
      logger.L2.debug(`Cannot shift bond before first revenue date`, {
        context: 'store:storeOperations',
      });
      return null;
    }
  }

  modifiedBonds[bondIndex] = targetBond;

  return cloneConfigurationWithChanges(project, configurationId, {
    bondSeries: modifiedBonds,
  });
}

/**
 * Quick action: Duplicates a bond with a time offset
 *
 * @param project - Current project state
 * @param configurationId - Configuration containing the bond
 * @param bondId - ID of the bond to duplicate
 * @param offsetYears - Years to offset the duplicate (1, 2, or 3)
 * @returns Updated project and new configuration ID
 */
export function cloneWithBondDuplicate(
  project: Project,
  configurationId: string,
  bondId: string,
  offsetYears: 1 | 2 | 3
): { updatedProject: Project; newConfigurationId: string } | null {
  const configuration = project.configurations?.find((config) => config.id === configurationId);
  if (!configuration) {
    logger.L2.debug(`Configuration ${configurationId} not found`, {
      context: 'store:storeOperations',
    });
    return null;
  }

  const bondToDuplicate = configuration.bondSeries.find((b) => b.id === bondId);
  if (!bondToDuplicate) {
    logger.L2.debug(`Bond ${bondId} not found`, { context: 'store:storeOperations' });
    return null;
  }

  // Create a duplicate bond with new ID and shifted dates
  const duplicatedBond: BondSeries = {
    ...bondToDuplicate,
    id: `${bondToDuplicate.id}-dup-${Date.now()}`,
    name: `${bondToDuplicate.name} (+${offsetYears}yr)`,
    issueDate: shiftDateByYears(bondToDuplicate.issueDate, offsetYears),
  };

  // Add the duplicate to existing bonds (ADDITIVE)
  const modifiedBonds = [...configuration.bondSeries, duplicatedBond];

  return cloneConfigurationWithChanges(project, configurationId, {
    bondSeries: modifiedBonds,
  });
}

/**
 * Quick action: Updates the target value for a specific bond
 */
export function cloneWithTargetValue(
  project: Project,
  configurationId: string,
  bondId: string,
  targetValue: number | null
): { updatedProject: Project; newConfigurationId: string } | null {
  const configuration = project.configurations?.find((config) => config.id === configurationId);
  if (!configuration) {
    logger.L2.debug(`Configuration ${configurationId} not found`, {
      context: 'store:storeOperations',
    });
    return null;
  }

  const bondIndex = configuration.bondSeries.findIndex((b) => b.id === bondId);
  if (bondIndex === -1) {
    logger.L2.debug(`Bond ${bondId} not found`, { context: 'store:storeOperations' });
    return null;
  }

  // Clone bonds array and update target value
  const modifiedBonds = [...configuration.bondSeries];
  const targetBond = { ...modifiedBonds[bondIndex] };

  // Update or remove target information based on value
  if (targetValue && targetValue > 0) {
    targetBond.targetInformation = {
      principalTargetAmount: targetValue,
      targetType: TargetType.DOES_NOT_EXCEED,
    };
  } else {
    // Remove target information if value is null or 0
    delete targetBond.targetInformation;
  }

  modifiedBonds[bondIndex] = targetBond;

  return cloneConfigurationWithChanges(project, configurationId, {
    bondSeries: modifiedBonds,
  });
}

/**
 * Determines if a new configuration should be created based on change type
 */
export function shouldCreateNewConfiguration(changeType: string): boolean {
  const configurationTriggers = [
    'bond_added',
    'bond_removed',
    'bond_modified',
    'millage_changed',
    'drf_changed',
    'quick_action',
  ];

  return configurationTriggers.includes(changeType);
}

// Helper function to shift dates
function shiftDateByYears(dateString: string, years: number): string {
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString().split('T')[0];
}
